/**
 * PDF Downloader Utility
 * Converts base64 encoded PDF buffer to Blob and triggers browser download
 */

/**
 * Convert base64 PDF buffer to Blob and trigger download
 * @param {string} base64Buffer - Base64 encoded PDF buffer from backend
 * @param {string} filename - Name for the downloaded file
 * @param {string} mimeType - MIME type (default: application/pdf)
 * @throws {Error} If buffer conversion or download fails
 */
export function downloadPDFFromBuffer(base64Buffer, filename, mimeType = 'application/pdf') {
    if (!base64Buffer) {
        throw new Error('PDF buffer is required');
    }

    if (!filename) {
        throw new Error('Filename is required');
    }

    try {
        // Convert base64 to binary string
        const binaryString = window.atob(base64Buffer);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        
        // Convert binary string to byte array
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create Blob from byte array
        const blob = new Blob([bytes], { type: mimeType });
        
        // Create temporary download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 100);
        
    } catch (error) {
        console.error('Error downloading PDF from buffer:', error);
        throw new Error(`Failed to download PDF: ${error.message}`);
    }
}

/**
 * Check if browser supports the required APIs
 * @returns {boolean} True if browser supports PDF download
 */
export function isPDFDownloadSupported() {
    return !!(
        window.atob &&
        window.Blob &&
        window.URL &&
        window.URL.createObjectURL &&
        document.createElement
    );
}

/**
 * Alternative download method using data URL (fallback)
 * @param {string} base64Buffer - Base64 encoded PDF buffer
 * @param {string} filename - Name for the downloaded file
 */
export function downloadPDFFromBufferFallback(base64Buffer, filename) {
    try {
        const dataUrl = `data:application/pdf;base64,${base64Buffer}`;
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
        }, 100);
    } catch (error) {
        console.error('Error in fallback PDF download:', error);
        throw new Error(`Failed to download PDF using fallback method: ${error.message}`);
    }
}

/**
 * Download PDF with automatic fallback
 * @param {string} base64Buffer - Base64 encoded PDF buffer
 * @param {string} filename - Name for the downloaded file
 * @param {string} mimeType - MIME type
 */
export function downloadPDFSafe(base64Buffer, filename, mimeType = 'application/pdf') {
    try {
        if (isPDFDownloadSupported()) {
            downloadPDFFromBuffer(base64Buffer, filename, mimeType);
        } else {
            downloadPDFFromBufferFallback(base64Buffer, filename);
        }
    } catch (error) {
        console.error('All PDF download methods failed:', error);
        throw error;
    }
}
